import type { PageContext } from '../../lib/types';
// Admin: Usage & Costs, Feature Flags, Audit Log, System Settings (backend §76–82).
import { html, toast } from '../../lib/dom';
import { icon } from '../../components/icons';
import { pageHead, table, kpi, select, empty } from '../../components/ui';
import { columnChart, bindCharts } from '../../components/chart';
import { usageBreakdown, dailyCost, overview, listFlags, toggleFlag, auditEvents } from '../../api/admin';
import { db, byId } from '../../api/core';
import { getUser } from '../../api/workspace';
import { money, shortDate, dateTime, num } from '../../lib/format';

const BY = [['project_id', 'Project'], ['flow_id', 'Flow'], ['provider', 'Provider'], ['model', 'Model'], ['metric_type', 'Metric']];
const keyLabel = (by?: any, key?: any) => (by === 'project_id' ? byId('projects', key)?.name : by === 'flow_id' ? byId('flows', key)?.name : key) || key;

export function renderUsage(ctx: PageContext) {
  const by = ctx.query.by || 'project_id';
  const o = overview();
  const rows = usageBreakdown(by);
  const provider = db.usage_events.reduce((a?: any, u?: any) => a + u.provider_cost, 0);
  const days = dailyCost(30);
  const body = html`<div class="page">
    ${pageHead({ eyebrow: 'Platform admin', title: 'Usage & Costs', lede: 'Every execution writes a usage event. Costs roll up Organization → Workspace → Project → Flow → Run → Node.' })}
    <div class="grid c4">${kpi('Cost today', money(o.costToday))}${kpi('Cost this month', money(o.costMonth))}${kpi('Provider cost', money(provider), 'what we pay')}${kpi('Margin', money(o.costMonth - provider), `${((1 - provider / o.costMonth) * 100).toFixed(0)}%`)}</div>
    <section class="card pad-lg">${columnChart(days.map((d?: any) => ({ label: shortDate(d.day), value: d.cost })), { title: 'Internal cost per day, last 30 days', fmt: (v?: any) => money(v), id: 'cost30', height: 200 })}</section>
    <section class="section">
      <div class="toolbar"><h2 style="margin-right:8px">Breakdown</h2><div class="seg">${BY.map(([k, l]: any) => html`<button class="${by === k ? 'on' : ''}" data-param="by" data-value="${k}">${l}</button>`)}</div></div>
      <div class="card">${table([
        { label: BY.find(([k]: any) => k === by)[1], render: (r?: any) => keyLabel(by, r.key) },
        { label: 'Events', num: true, render: (r?: any) => num(r.events) },
        { label: 'Provider cost', num: true, render: (r?: any) => money(r.provider_cost, 3) },
        { label: 'Internal cost', num: true, render: (r?: any) => money(r.internal_cost, 3) },
        { label: 'Share', render: (r?: any) => html`<div class="bar" style="width:140px" title="${((r.internal_cost / o.costMonth) * 100).toFixed(1)}%"><span style="width:${((r.internal_cost / o.costMonth) * 100).toFixed(1)}%"></span></div>` },
      ], rows)}</div>
    </section>
  </div>`;
  return { title: 'Admin · Usage', body, mount: (root?: any) => bindCharts(root) };
}

export function renderFlags(ctx: PageContext) {
  const scopeName = (ids?: any, table?: any) => ids.map((id?: any) => byId(table, id)?.name || id);
  const body = html`<div class="page">
    ${pageHead({ eyebrow: 'Platform admin', title: 'Feature Flags', lede: 'Enable features globally, per organization or per workspace. Every change is written to the audit log.' })}
    <div class="card">${table([
      { label: 'Flag', render: (f?: any) => html`<div class="col" style="gap:0"><span class="mono" style="font-weight:500">${f.key}</span><span class="muted" style="font-size:12px">${f.description}</span></div>` },
      { label: 'Global', render: (f?: any) => html`<button class="toggle ${f.enabled_globally ? 'on' : ''}" data-flag="${f.id}" aria-pressed="${f.enabled_globally}" aria-label="Toggle ${f.key}"></button>` },
      { label: 'Organizations', render: (f?: any) => scopeName(f.organization_ids, 'organizations').map((n?: any) => html`<span class="chip" style="margin-right:4px">${n}</span>`) },
      { label: 'Workspaces', render: (f?: any) => scopeName(f.workspace_ids, 'workspaces').map((n?: any) => html`<span class="chip" style="margin-right:4px">${n}</span>`) },
      { label: 'Updated', render: (f?: any) => shortDate(f.updated_at) },
    ], listFlags())}</div>
  </div>`;
  return { title: 'Admin · Feature Flags', body, mount: (root?: any) => root.querySelectorAll('[data-flag]').forEach((b?: any) => b.addEventListener('click', () => { toggleFlag(b.dataset.flag); toast('Flag updated · audit event written'); ctx.rerender(); })) };
}

export function renderAudit(ctx: PageContext) {
  const rows = auditEvents({ action: ctx.query.action, entityType: ctx.query.entity });
  const all = auditEvents();
  const body = html`<div class="page">
    ${pageHead({ eyebrow: 'Platform admin', title: 'Audit Log', lede: 'Who changed what, with before/after snapshots.' })}
    <div class="toolbar">
      ${select('action', [...new Set(all.map((e?: any) => e.action))].map((a?: any) => [a, a]), ctx.query.action, { placeholder: 'Any action' })}
      ${select('entity', [...new Set(all.map((e?: any) => e.entity_type))].map((a?: any) => [a, a]), ctx.query.entity, { placeholder: 'Any entity' })}
    </div>
    <div class="card">${rows.length ? table([
      { label: 'When', render: (e?: any) => dateTime(e.created_at) },
      { label: 'Actor', render: (e?: any) => getUser(e.user_id).name },
      { label: 'Action', mono: true, render: (e?: any) => e.action },
      { label: 'Entity', mono: true, render: (e?: any) => `${e.entity_type} · ${e.entity_id}` },
      { label: 'After', mono: true, render: (e?: any) => JSON.stringify(e.after || {}) },
    ], rows) : empty('No events')}</div>
  </div>`;
  return { title: 'Admin · Audit', body };
}

export function renderSystem() {
  const body = html`<div class="page narrow">
    ${pageHead({ eyebrow: 'Platform admin', title: 'System Settings' })}
    <div class="card pad-lg"><div class="form">
      <label class="row"><button class="toggle" data-toggle></button>Maintenance mode (new runs are queued, not executed)</label>
      <div class="form-row"><div class="field"><label>Default node timeout (s)</label><input class="input" type="number" value="120"></div><div class="field"><label>Max concurrent runs per org</label><input class="input" type="number" value="20"></div></div>
      <div class="form-row"><div class="field"><label>Run history retention (days)</label><input class="input" type="number" value="90"></div><div class="field"><label>Storage bucket</label><input class="input mono" value="s3://cf-prod-outputs"></div></div>
      <div class="field"><label>Runtime</label><select class="select"><option>DAG only (loops disabled)</option><option>DAG + explicit loop nodes</option></select><span class="hint">Flows with cycles are rejected at validation.</span></div>
      <div class="banner info">${icon('admin')}<div>platform_admin is a separate internal role and is never granted through organization membership.</div></div>
      <div><button class="btn primary" data-save>Save</button></div>
    </div></div>
  </div>`;
  return {
    title: 'Admin · System', body,
    mount(root?: any) {
      root.querySelectorAll('[data-toggle]').forEach((t?: any) => t.addEventListener('click', () => t.classList.toggle('on')));
      root.querySelector('[data-save]').addEventListener('click', () => toast('System settings saved'));
    },
  };
}
